// src/lib/analytics.js
// Central analytics service — all PostHog calls go through here.
// Never call posthog directly from components.
// No-ops in development (process.env.NODE_ENV !== "production").

import posthog from "posthog-js";

const isProd = process.env.NODE_ENV === "production";

// ── Identity ──────────────────────────────────────────────────

export function identifyUser(userId, properties = {}) {
  if (!isProd) return;
  posthog.identify(userId, {
    tier:              properties.tier            ?? "free",
    created_at:        properties.createdAt,
    has_portfolio:     properties.hasPortfolio    ?? false,
    holding_count:     properties.holdingCount    ?? 0,
  });
}

export function resetUser() {
  if (!isProd) return;
  posthog.reset();
}

// ── Page views ────────────────────────────────────────────────

export function trackPageView(path) {
  if (!isProd) return;
  posthog.capture("$pageview", { $current_url: path });
}

// ── Tab view (app uses tabs not URL routes internally) ────────

export function trackTabView(tabId) {
  if (!isProd) return;
  posthog.capture("tab_viewed", { tab: tabId });
}

// ── Onboarding funnel ─────────────────────────────────────────

export function trackTermsAccepted() {
  if (!isProd) return;
  posthog.capture("onboarding_terms_accepted");
}

export function trackPortfolioValueSet(method) {
  if (!isProd) return;
  posthog.capture("onboarding_portfolio_value_set", { method });
}

export function trackOnboardingCompleted(durationSeconds) {
  if (!isProd) return;
  posthog.capture("onboarding_completed", { duration_seconds: durationSeconds });
}

export function trackOnboardingDropped(step) {
  if (!isProd) return;
  posthog.capture("onboarding_dropped", { dropped_at_step: step });
}

// ── Portfolio ─────────────────────────────────────────────────

export function trackHoldingAdded(ticker) {
  if (!isProd) return;
  posthog.capture("holding_added", { ticker });
}

export function trackHoldingRemoved(ticker) {
  if (!isProd) return;
  posthog.capture("holding_removed", { ticker });
}

// ── Module events ─────────────────────────────────────────────

export function trackBacktestRun(assetCount) {
  if (!isProd) return;
  posthog.capture("backtest_run", { asset_count: assetCount });
}

export function trackScreenerFilterApplied(filterCount) {
  if (!isProd) return;
  posthog.capture("screener_filter_applied", { filter_count: filterCount });
}

export function trackOptimizerRun(objective) {
  if (!isProd) return;
  posthog.capture("optimizer_run", { objective: objective ?? "sharpe" });
}

export function trackMonteCarloRun(horizonYears) {
  if (!isProd) return;
  posthog.capture("monte_carlo_run", { horizon_years: horizonYears });
}

// ── Upgrade events ────────────────────────────────────────────

export function trackUpgradePromptSeen(source) {
  if (!isProd) return;
  posthog.capture("upgrade_prompt_seen", { source });
}

export function trackUpgradeClicked(source) {
  if (!isProd) return;
  posthog.capture("upgrade_clicked", { source });
}

// ── Watchlist ─────────────────────────────────────────────────

export function trackWatchlistItemAdded() {
  if (!isProd) return;
  posthog.capture("watchlist_item_added");
}
